import { useEffect, useState } from 'react';
import { toast } from '../../../util/toasts';
import { Invoice } from '../../../clients/quipli-service/invoice/invoice';
import { FiltersModal } from '../../../components/modal/filters-modal';
import { Typography } from '../../../components/typography/typography';
import { displayMoneyFormat } from '../../../util/util';
import { Checkbox } from '../../../components/checkbox/checkbox';
import { Field, FieldLabel } from '../../../components/field/field';
import { useInvoiceApiV1 } from '../../../clients/quipli-service/invoice/invoice-api-v1';
import { TextArea } from '../../../components/textarea/textarea';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { QuantityInput } from '../../../customer/product-form/quantity-input/quantity-input';
import './add-line-item-modal.scss';
import {
  Payment,
  PaymentMethodType,
  PaymentStatus,
  RefundReason,
} from '../../../clients/quipli-service/payment/payment';
import { PaymentMethodRequestV2 } from '../../../clients/quipli-service/payment/payment-v2';
import { PaymentMethodSelectorV2 } from '../../payment/payment-method-selector-v2';
import { usePaymentApiV2 } from '../../../clients/quipli-service/payment/payment-api-v2';
import classNames from 'classnames';
import { components } from 'react-select';
import { SelectForm } from '../../../components/select-form/select-form';
import { Radio } from '../../../components/radio/radio';
import { useOrderApiV1 } from '../../../clients/quipli-service/order/order-api-v1';
import { Order } from '../../../clients/quipli-service/order/order';
import {
  GeneralLedgerAccount,
  GeneralLedgerAccountType,
  ItemGeneralLedgerAccount,
  ItemType,
} from '../../../clients/quipli-service/accounting/accounting';
import { useAccountingApiV1 } from '../../../clients/quipli-service/accounting/accounting-api-v1';
import { PaymentContextProvider } from '../../../context/payment-context';
import { WorkOrder } from '../../../clients/quipli-service/work-order/work-order';
import { TaxCategory } from '../../../clients/quipli-service/tax/tax';
import { useTaxApiV1 } from '../../../clients/quipli-service/tax/tax-api-v1';
import { LineItemTax, lineItemTaxCalculation } from '../../collectibles/components/edit-line-item-modal';
import { Tooltip } from '../../../components/tooltip/tooltip';
import Icon from '../../../components/icons/icon';
import { ButtonGroup } from '../../../components/button-group/button-group';

type PaymentMethodToRefund = {
  methodType: string;
  detail?: string;
  amount: number;
};

export const AddLineItemModal = ({
  invoice,
  isOpen,
  close,
  currency,
}: {
  invoice: Invoice;
  isOpen: boolean;
  close: (payments?: any[]) => void;
  currency: string;
}) => {
  const { getItemGeneralLedgerAccount, listAccounts } = useAccountingApiV1();
  const { createLineItem } = useInvoiceApiV1();
  const { createPayment, refundInvoice, unapplyPayment } = usePaymentApiV2();
  const { searchTaxCategories } = useTaxApiV1();
  const { sendEmailAsync } = useAPI();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPaymentConfirmation, setIsPaymentConfirmation] = useState<boolean>(false);
  const [amount, setAmount] = useState<number>(0);
  const [isRefund, setIsRefund] = useState<boolean>();
  const [taxApplicable, setTaxApplicable] = useState<boolean>(true);
  const [lineItemName, setLineItemName] = useState<string>('');
  const [comments, setComments] = useState<string>();
  const [lineItemQuantity, setLineItemQuantity] = useState<string>('1');
  const [lineItemQuantityNumber, setLineItemQuantityNumber] = useState<number>(1);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodRequestV2 | undefined>();
  const [refundReason, setRefundReason] = useState<RefundReason>();
  const [refundMethod, setRefundMethod] = useState<PaymentMethodToRefund>();
  const [lineItemId, setLineItemId] = useState<string>();
  const [payments, setPayments] = useState<Payment[]>();
  const [facilitate, setFacilitate] = useState<boolean>(false);
  const [accounts, setAccounts] = useState<GeneralLedgerAccount[]>();
  const [generalLedgerAccount, setGeneralLedgerAccount] = useState<GeneralLedgerAccount>();
  const [defaultUpchargeItemGeneralLedgerAccount, setDefaultUpchargeItemGeneralLedgerAccount] =
    useState<ItemGeneralLedgerAccount>();
  const [defaultRefundItemGeneralLedgerAccount, setDefaultRefundItemGeneralLedgerAccount] =
    useState<ItemGeneralLedgerAccount>();
  const [taxCategory, setTaxCategory] = useState<TaxCategory | undefined>();
  const [taxCategories, setTaxCategories] = useState<TaxCategory[]>();
  const [action, setAction] = useState<'refund' | 'unapply'>('refund');

  useEffect(() => {
    setLineItemQuantityNumber(parseFloat(lineItemQuantity));
  }, [lineItemQuantity]);

  useEffect(() => {
    Promise.all([
      listAccounts(false, GeneralLedgerAccountType.Income).then(setAccounts),
      getItemGeneralLedgerAccount(ItemType.Upcharge).then(setDefaultUpchargeItemGeneralLedgerAccount),
      getItemGeneralLedgerAccount(ItemType.Refund).then(setDefaultRefundItemGeneralLedgerAccount),
      invoice.taxCode && searchTaxCategories({}).then(setTaxCategories),
    ]).then(() => setIsRefund(false));
  }, []);

  useEffect(() => {
    if (isRefund && accounts && defaultRefundItemGeneralLedgerAccount) {
      setGeneralLedgerAccount(
        accounts.find((a) => a.id === defaultRefundItemGeneralLedgerAccount.generalLedgerAccountId),
      );
    } else if (!isRefund && accounts && defaultUpchargeItemGeneralLedgerAccount) {
      setGeneralLedgerAccount(
        accounts.find((a) => a.id === defaultUpchargeItemGeneralLedgerAccount.generalLedgerAccountId),
      );
    }
  }, [isRefund]);

  function onGeneralLedgerAccountChange(selected: { label: string; value: string }) {
    setGeneralLedgerAccount(accounts?.find((a) => a.id === selected.value));
  }

  function onTaxCategoryChange(selected?: { label: string; value: string }) {
    if (selected) {
      setTaxCategory(taxCategories?.find((a) => a.id === selected.value));
    } else {
      setTaxCategory(undefined);
    }
  }

  const maxRefund = taxApplicable ? -invoice.subtotal : -invoice.total;

  const taxOverride = isRefund && amount * invoice.taxRate > invoice.tax ? invoice.tax : undefined;
  async function addLineItem() {
    setIsLoading(true);
    if (amount > 0 || (amount < 0 && maxRefund <= amount)) {
      try {
        const lineItem = await createLineItem(invoice.id, {
          name: lineItemName,
          description: comments,
          unitPrice: isRefund ? -1 * amount : amount,
          quantity: lineItemQuantityNumber,
          taxable: taxApplicable,
          taxOverride: isRefund && taxOverride ? -1 * taxOverride : taxOverride,
          generalLedgerAccountId: generalLedgerAccount?.id,
          taxCategoryId: taxCategory?.id,
        });
        setLineItemId(lineItem.id);
        toast('Line item successfully added');
        if (invoice.documentType == 'Invoice') {
          setIsPaymentConfirmation(true);
        } else {
          close([]);
        }
      } catch (ex: any) {
        toast(ex.message.msg || ex.message, 'error');
      } finally {
        setIsLoading(false);
      }
    }
  }

  async function postPayment(payments?: Payment[]) {
    setFacilitate(false);
    if (payments) {
      const payment = payments[0];
      await sendEmailAsync({
        templateName: 'order_upcharge_email_user',
        orderId: invoice.type === 'Order' ? parseInt(invoice.typeId) : undefined,
        invoiceId: invoice.id,
        lineItemId,
        paymentId: payment.id,
      });
      close([payment]);
    }
  }

  async function submitPayment() {
    setIsLoading(true);
    try {
      if (!lineItemId) {
        return;
      }

      const total = Number(
        (
          Math.abs(amount) * lineItemQuantityNumber +
          Math.abs(
            lineItemTaxCalculation(
              {
                name: lineItemName,
                description: comments,
                unitPrice: isRefund ? -1 * amount : amount,
                quantity: lineItemQuantityNumber,
                taxable: taxApplicable,
                taxOverride: isRefund && taxOverride ? -1 * taxOverride : taxOverride,
                generalLedgerAccountId: generalLedgerAccount?.id,
                taxCategoryId: taxCategory?.id,
              },
              invoice,
            ),
          )
        ).toFixed(2),
      );
      let result;
      if (isRefund && !refundMethod) {
        toast('Please select a refund method', 'error');
        return;
      } else if (isRefund && action === 'unapply') {
        result = await unapplyPayment(invoice.id, {
          amount: total,
          reason: RefundReason.Other,
          method:
            (Object.keys(PaymentMethodType).find(
              (m) => refundMethod?.methodType == m.toString(),
            ) as PaymentMethodType) || PaymentMethodType.Other,
          detail: refundMethod?.detail,
        });
        toast('Payment successfully unapplied');
        close([result]);
      } else if (isRefund && !refundReason) {
        toast('Please select a refund reason', 'error');
        return;
      } else if (isRefund && action === 'refund') {
        result = await refundInvoice(invoice.id, {
          amount: total,
          reason: refundReason!,
          method:
            (Object.keys(PaymentMethodType).find(
              (m) => refundMethod?.methodType == m.toString(),
            ) as PaymentMethodType) || PaymentMethodType.Other,
          detail: refundMethod?.detail,
        });
        toast('Refund successfully processed');
        await sendEmailAsync({
          templateName: 'order_refund_issued_email_user',
          orderId: invoice.type === 'Order' ? parseInt(invoice.typeId) : undefined,
          workOrderId: invoice.type === 'WorkOrder' ? invoice.typeId : undefined,
          invoiceId: invoice.id,
          lineItemId,
          paymentId: result[0]?.id,
        });
        close([result]);
      } else if (!paymentMethod) {
        toast('Please select a payment method', 'error');
        return;
      } else {
        const payment = await createPayment({
          invoiceIds: [invoice.id],
          amount: total,
          paymentMethod: paymentMethod,
          untaxed: !taxApplicable,
          userId: invoice.customer.id,
        });
        toast('Payment successfully processed');

        if (paymentMethod.type === PaymentMethodType.Stripe && paymentMethod.terminalId) {
          setFacilitate(true);
          setPayments([payment]);
          return;
        } else {
          if (payment) {
            await sendEmailAsync({
              templateName: 'order_upcharge_email_user',
              orderId: invoice.type === 'Order' ? parseInt(invoice.typeId) : undefined,
              workOrderId: invoice.type === 'WorkOrder' ? invoice.typeId : undefined,
              invoiceId: invoice.id,
              lineItemId,
              paymentId: payment.id,
            });
          }
          close([payment]);
        }
      }
    } catch (ex: any) {
      toast(ex.message.msg || ex.message || `Error processing ${isRefund ? 'refund' : 'payment'}`, 'error');
    } finally {
      setIsLoading(false);
    }
  }
  return (
    <FiltersModal
      isOpen={isOpen}
      title={
        isPaymentConfirmation
          ? `              Would you like to ${isRefund ? 'refund' : 'charge'} the payment method now?`
          : `Add Line Item to Invoice #${invoice.invoiceNumber}`
      }
      confirmText={
        isPaymentConfirmation
          ? `${isRefund ? 'Refund' : 'Upcharge'} ${displayMoneyFormat(
              Math.abs(amount) * lineItemQuantityNumber +
                Math.abs(
                  lineItemTaxCalculation(
                    {
                      name: lineItemName,
                      description: comments,
                      unitPrice: isRefund ? -1 * amount : amount,
                      quantity: lineItemQuantityNumber,
                      taxable: taxApplicable,
                      taxOverride: isRefund && taxOverride ? -1 * taxOverride : taxOverride,
                      generalLedgerAccountId: generalLedgerAccount?.id,
                      taxCategoryId: taxCategory?.id,
                    },
                    invoice,
                  ),
                ),
              currency,
            )} Now`
          : 'Add Line Item'
      }
      cancelText={isPaymentConfirmation ? 'No' : 'Cancel'}
      allowConfirm={
        (!isPaymentConfirmation ||
          (isPaymentConfirmation && (isRefund ? !!refundMethod && !!refundReason : !!paymentMethod))) &&
        !isLoading &&
        !!lineItemName &&
        (amount < 0 ? maxRefund <= amount : amount > 0)
      }
      allowCancel={!isLoading}
      showConfirmButtonFirst={false}
      onConfirm={() => {
        isPaymentConfirmation ? submitPayment() : addLineItem();
      }}
      onCancel={() => {
        close(isPaymentConfirmation ? [lineItemId] : undefined);
      }}
    >
      <div className="min-w-full w-[370px] flex flex-col gap-2 items-stretch">
        {!isPaymentConfirmation ? (
          <>
            <div className="flex gap-5 my-2">
              <div className="flex gap-1 items-center">
                <Radio isChecked={!isRefund} onChange={() => setIsRefund(false)} />
                <FieldLabel label="Upcharge" />
              </div>
              {invoice.documentType === 'Invoice' && (
                <div className="flex gap-1 items-center">
                  <Radio isChecked={isRefund} onChange={() => setIsRefund(true)} />
                  <FieldLabel label="Refund" />
                </div>
              )}
            </div>

            <FieldLabel label="Line Item Name" />
            <Field type="text" value={lineItemName} onChange={(n) => setLineItemName(n)} />

            <FieldLabel label="General Ledger Account" />
            <Field
              type="dropdown"
              value={{
                label: generalLedgerAccount?.name,
                value: generalLedgerAccount?.id,
              }}
              onChange={(a) => onGeneralLedgerAccountChange(a)}
              options={accounts?.map((a) => ({ label: a.name, value: a.id }))}
            />

            {invoice.taxCode && taxCategories && taxCategories.length > 0 && (
              <>
                <FieldLabel label="Tax Category" />
                <Field
                  type="dropdown"
                  value={{
                    label: taxCategory?.name,
                    value: taxCategory?.id,
                  }}
                  onChange={(a) => onTaxCategoryChange(a)}
                  options={taxCategories?.map((a) => ({ label: a.name, value: a.id }))}
                  isClearable={true}
                />
              </>
            )}

            <div className="flex justify-between">
              <Field
                label="Amount"
                invalid={amount < 0 && maxRefund > amount * lineItemQuantityNumber}
                type="currency"
                value={amount}
                onChange={(a) => {
                  if (Number.isNaN(a) && !amount) {
                    setAmount(NaN);
                    setAmount(NaN);
                  } else setAmount(a);
                }}
                maximumFractionDigits={2}
              />

              <div>
                <FieldLabel label="Quantity" />
                <QuantityInput
                  name="Quantity"
                  value={lineItemQuantity}
                  onChange={(q) => setLineItemQuantity(q ?? '1')}
                />
              </div>
            </div>
            <div className="mt-2 text-right text-[var(--grey-500)] text-sm">
              Subtotal: {displayMoneyFormat(amount * lineItemQuantityNumber, currency)}
            </div>

            {/* don't allow crediting the invoice for more than the invoice total */}
            {amount * lineItemQuantityNumber < 0 && maxRefund > amount * lineItemQuantityNumber && (
              <Typography type="text-sm" weight="regular" className="text-[var(--error-500)]">
                Refund amount cannot exceed {displayMoneyFormat(maxRefund, currency)}
              </Typography>
            )}

            <div className="flex justify-between mb-3">
              <Checkbox
                isChecked={taxApplicable}
                onChange={(checked) => setTaxApplicable(checked)}
                label="Tax applicable"
              />
              <LineItemTax
                lineItem={{
                  name: lineItemName,
                  description: comments,
                  unitPrice: isRefund ? -1 * amount : amount,
                  quantity: lineItemQuantityNumber,
                  taxable: taxApplicable,
                  generalLedgerAccountId: generalLedgerAccount?.id,
                  taxCategoryId: taxCategory?.id,
                }}
                invoice={invoice}
                currency={currency}
              />
            </div>

            <Typography type="text-md" weight="medium" className="text-[var(--grey-700)]">
              Comments (optional)
            </Typography>
            <TextArea value={comments} onChange={(c) => setComments(c.target.value)} />
          </>
        ) : (
          <>
            {isRefund ? (
              <>
                <RefundPaymentSection
                  total={
                    Math.abs(amount) * lineItemQuantityNumber +
                    (taxApplicable
                      ? isRefund && Math.abs(amount * lineItemQuantityNumber) * invoice.taxRate > invoice.tax
                        ? invoice.tax
                        : Math.abs(amount * lineItemQuantityNumber) * invoice.taxRate
                      : 0)
                  }
                  invoice={invoice}
                  currency={currency}
                  selectedReason={refundReason}
                  refundAction={action}
                  setSelectedReason={setRefundReason}
                  selectedMethod={refundMethod}
                  setSelectedMethod={(m) => setRefundMethod(m)}
                  setRefundAction={setAction}
                />
              </>
            ) : (
              <PaymentContextProvider>
                <PaymentMethodSelectorV2
                  user={invoice.customer}
                  allowedPaymentMethods={[
                    PaymentMethodType.Stripe,
                    PaymentMethodType.Cash,
                    PaymentMethodType.Check,
                    PaymentMethodType.Other,
                    PaymentMethodType.Custom,
                  ]}
                  paymentMethod={paymentMethod}
                  setPaymentMethod={(pm) =>
                    [
                      PaymentMethodType.Stripe,
                      PaymentMethodType.Cash,
                      PaymentMethodType.Check,
                      PaymentMethodType.Other,
                      PaymentMethodType.Custom,
                    ].indexOf(pm.type) >= 0 && setPaymentMethod(pm as PaymentMethodRequestV2)
                  }
                  locationId={invoice.locationId}
                  setPayments={setPayments}
                  facilitate={facilitate}
                  payments={payments}
                  onComplete={postPayment}
                  setAmount={setAmount}
                  balance={invoice.balance}
                />
              </PaymentContextProvider>
            )}
          </>
        )}
      </div>
    </FiltersModal>
  );
};

const RefundPaymentSection = ({
  total,
  invoice,
  currency,
  selectedReason,
  refundAction,
  setSelectedReason,
  selectedMethod,
  setSelectedMethod,
  setRefundAction,
}: {
  total: number;
  invoice: Invoice;
  currency: string;
  selectedReason: RefundReason | undefined;
  refundAction: 'refund' | 'unapply';
  setSelectedReason: (r: RefundReason) => void;
  selectedMethod: PaymentMethodToRefund | undefined;
  setSelectedMethod: (method: PaymentMethodToRefund | undefined) => void;
  setRefundAction: (action: 'refund' | 'unapply') => void;
}) => {
  const { getOrderById } = useOrderApiV1();
  const { getWorkOrderById } = useAPI();
  const [order, setOrder] = useState<Order | WorkOrder>();
  const [refundFromOptions, setRefundFromOptions] = useState<PaymentMethodToRefund[]>([]);

  useEffect(() => {
    if (invoice.type === 'Order') {
      getOrderById(parseInt(invoice.typeId))
        .then(setOrder)
        .catch((e: any) => {
          console.log(e);
          toast(`Failed to load order`, 'error');
        });
    } else if (invoice.type === 'WorkOrder') {
      getWorkOrderById(invoice.typeId)
        .then(setOrder)
        .catch((e: any) => {
          console.log(e);
          toast(`Failed to load work order`, 'error');
        });
    }
  }, []);

  useEffect(() => {
    if (order && order.invoices) {
      const methodTotal: Record<
        string,
        {
          methodType: string;
          amount: number;
        }
      > = {};
      order.invoices
        .flatMap((inv) => inv.payments)
        .forEach((p) => {
          if (p.status !== PaymentStatus.succeeded) return;

          let method: string;

          switch (p.method) {
            case PaymentMethodType.Stripe: {
              method = p.methodDetail || p.method;
              break;
            }
            default: {
              method = p.method;
              break;
            }
          }

          if (methodTotal[method] !== undefined) {
            methodTotal[method].amount += p.amount;
          } else {
            methodTotal[method] = {
              methodType: p.method,
              amount: p.amount,
            };
          }
        });
      setRefundFromOptions(
        Object.entries(methodTotal)
          .filter(([record, { amount }]) => amount > 0 && amount >= total && record !== 'QBO')
          .map(([record, { methodType, amount }]) => {
            let detail: string | undefined;
            if (methodType === 'Stripe') {
              detail = record;
            }

            return {
              methodType,
              amount,
              detail,
            };
          }),
      );
    }
  }, [order]);

  useEffect(() => {
    if (!selectedMethod) {
      const defaultMethod = refundFromOptions.length
        ? refundFromOptions.sort((a, b) => b.amount - a.amount)[0]
        : undefined;
      if (defaultMethod) {
        setSelectedMethod(defaultMethod);
      }
    }
  }, [refundFromOptions, selectedMethod]);

  const refundReasonsOptions = Object.values(RefundReason).map((r) => {
    return { id: r, label: r };
  });

  const buttonItems: { label: string; value: 'refund' | 'unapply' }[] = [
    { label: 'Refund', value: 'refund' },
    { label: 'Unapply', value: 'unapply' },
  ];

  return (
    <div className="min-w-full w-[370px] flex flex-col gap-2 items-stretch">
      <div className="m-[8px] flex justify-center items-center" id={'refund-group'}>
        <Tooltip
          type="black"
          className="flex cursor-help"
          tooltip={
            "Whether to refund the payment to the customer or to unapply the balance from the invoice and into the customer's unapplied balance"
          }
        >
          <Icon icon={'InfoCircle'} size={'small'} />
        </Tooltip>
        <ButtonGroup<'refund' | 'unapply'>
          items={buttonItems}
          onClick={(value) => setRefundAction(value as 'refund' | 'unapply')}
          selected={refundAction}
        />
      </div>
      {refundAction == 'refund' ? (
        <>
          <Typography type="text-md" weight="regular" className="text-[var(--grey-500)]">
            Refunds take 5-10 days to appear on a customer statement
          </Typography>

          <Typography type="text-md" weight="medium" className="text-[var(--grey-700)] mt-[16px]">
            Reason
          </Typography>
          <SelectForm
            options={refundReasonsOptions}
            value={refundReasonsOptions.find((r) => r.id === selectedReason)}
            isSearchable={false}
            className={classNames('w-[380px] h-[44px]')}
            getOptionLabel={(r) => r.label}
            onChange={(r) => setSelectedReason(r?.id as RefundReason)}
            components={{
              SingleValue: (props) => (
                <components.SingleValue {...props}>
                  <Typography type="text-md" className="text-[var(--grey-800)]">
                    {props.children}
                  </Typography>
                </components.SingleValue>
              ),
            }}
          />
        </>
      ) : (
        <>
          <Typography type="text-md" weight="regular" className="text-[var(--grey-500)]">
            Payment will be removed from this invoice and available to be applied onto another invoice via the customer
            collectibles.
          </Typography>
          <Typography type="text-md" weight="medium" className="text-[var(--grey-700)] mt-[16px]">
            Reason
          </Typography>
          <SelectForm
            options={refundReasonsOptions}
            value={refundReasonsOptions.find((r) => r.id === selectedReason)}
            isSearchable={false}
            className={classNames('w-[380px] h-[44px]')}
            getOptionLabel={(r) => r.label}
            onChange={(r) => setSelectedReason(r?.id as RefundReason)}
            components={{
              SingleValue: (props) => (
                <components.SingleValue {...props}>
                  <Typography type="text-md" className="text-[var(--grey-800)]">
                    {props.children}
                  </Typography>
                </components.SingleValue>
              ),
            }}
          />
        </>
      )}

      {refundFromOptions.length >= 1 && (
        <>
          <Typography type="text-md" weight="medium" className="text-[var(--grey-700)] mt-[16px]">
            Refund from
          </Typography>
          <SelectForm
            options={refundFromOptions}
            value={selectedMethod}
            isSearchable={false}
            getOptionLabel={({ methodType, detail, amount }) =>
              `${detail || methodType} (${displayMoneyFormat(amount, currency)})`
            }
            getOptionValue={(op) => `${op.methodType}`}
            isDisabled={refundFromOptions.length < 1}
            className={classNames('w-[380px] h-[44px]')}
            onChange={(op) => setSelectedMethod(op as PaymentMethodToRefund)}
            components={{
              SingleValue: (props) => (
                <components.SingleValue {...props}>
                  <Typography type="text-md" className="text-[var(--grey-800)]">
                    {props.children}
                  </Typography>
                </components.SingleValue>
              ),
            }}
          />
        </>
      )}
    </div>
  );
};
